// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { formatDate, type Shipment, useEvolveGet, useEvolvePaginatedGetAll } from '@evolvemep/api-ts';
import {
  Card,
  Divider,
  Flex,
  Loader,
  Image as MantineImage,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { PrintableBlock, PrintableModal, PrintableTableFrame } from 'components/Mantine/Modals/PrintableModal';
import { formatQuantity } from 'helpers/formatQuantity';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';

type Props = ModalRegistryManagedProps & {
  shipment: Shipment;
};

const SectionCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card withBorder>
    <Card.Section withBorder bg="gray.1" px="md" py="xs">
      <Text fw={700} fz="sm">
        {title}
      </Text>
    </Card.Section>
    <Stack gap={2} pt="md">
      {children}
    </Stack>
  </Card>
);

const isNotBlank = (value: string | null | undefined): value is string => isNotNil(value) && value.trim().length > 0;

const SignatureLine = ({ label }: { label: string }) => (
  <Stack gap={4} mt="xl">
    <Divider />
    <Text fz="sm" c="dimmed">
      {label}
    </Text>
  </Stack>
);

const PackingSlipContents = ({ shipment }: Pick<Props, 'shipment'>) => {
  const { uoms, loading: loadingUoms } = useUnitOfMeasure();

  const { data: qrCodeResponse } = useEvolveGet(`shop/shipment/${shipment.shipmentId}/qrCode`, { lazy: false });
  const { data: facility } = useEvolveGet(`shop/facility/${shipment.facilityId}`, { lazy: false });

  const { data: items, loading: loadingItems } = useEvolvePaginatedGetAll(`shop/shipment/${shipment.shipmentId}/item`, {
    lazy: false,
  });

  // The UOM cache is part of the gate: printing before it resolves yields a slip with no units.
  if (isNil(qrCodeResponse) || isNil(facility) || loadingItems || loadingUoms) {
    return (
      <Stack align="center" p="xl">
        <Loader />
      </Stack>
    );
  }

  const { addressLine1, addressLine2, city, state, postalCode } = facility;
  const shipFromLines = [
    addressLine1,
    addressLine2,
    [[city, state].filter(isNotBlank).join(', '), postalCode].filter(isNotBlank).join(' '),
  ].filter(isNotBlank);

  return (
    <Stack gap="md">
      <Card withBorder>
        <Flex justify="space-between" align="center" gap="xs">
          <Stack gap={0}>
            <Text size="sm" c="dimmed">
              {shipment.projectName} · Ship date {formatDate(shipment.shipByDate, 'MM/dd/yyyy')}
            </Text>
            <Title order={2}>{shipment.identifier}</Title>
            <Text fw={500} fz="xl">
              {shipment.name}{' '}
              <Text span c="dimmed">
                ({items?.length} items)
              </Text>
            </Text>
          </Stack>
          <MantineImage
            src={`data:image/svg+xml;base64,${qrCodeResponse.rawData}`}
            alt="Shipping order packing slip QR code"
            maw={90}
          />
        </Flex>
      </Card>

      <SimpleGrid cols={2} spacing="md">
        <SectionCard title="Ship From">
          <Text fw={500} c="dimmed">
            {facility.facilityName}
          </Text>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{shipFromLines.join('\n')}</Text>
        </SectionCard>
        <SectionCard title="Ship To">
          <Text style={{ whiteSpace: 'pre-wrap' }}>{shipment.shipTo || '--'}</Text>
        </SectionCard>
      </SimpleGrid>

      <SectionCard title="Description">
        <Text style={{ whiteSpace: 'pre-wrap' }}>{shipment.description || '--'}</Text>
      </SectionCard>

      <Card withBorder p={0} bg="gray.0">
        <Flex>
          <Stack gap={0} p="md" flex={1}>
            <Text fz="xs" fw={500} c="dimmed">
              WORK ORDERS
            </Text>
            <Text>{shipment.relatedWorkOrders.length}</Text>
          </Stack>
          <Divider orientation="vertical" />
          <Stack gap={0} p="md" flex={1}>
            <Text fz="xs" fw={500} c="dimmed">
              ITEMS
            </Text>
            <Text>{items?.length}</Text>
          </Stack>
        </Flex>
      </Card>

      <PrintableTableFrame>
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th bg="gray.0" w="9%" />
              <Table.Th bg="gray.0" w="62%">
                Item Name
              </Table.Th>
              <Table.Th bg="gray.0" w="15%">
                Quantity
              </Table.Th>
              <Table.Th bg="gray.0" w="14%">
                UOM
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items?.map((item, index) => (
              <Table.Tr key={item.shipmentItemId}>
                <Table.Td>{index + 1}</Table.Td>
                <Table.Td>{item.workOrderItemName}</Table.Td>
                <Table.Td>{formatQuantity(item.allocatedQuantity)}</Table.Td>
                <Table.Td>{uoms?.find((uom) => uom.unitOfMeasureId === item.unitOfMeasureId)?.code ?? ''}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </PrintableTableFrame>

      <PrintableBlock>
        <SimpleGrid cols={2} spacing="xl">
          <SignatureLine label="Signature of Recipient" />
          <SignatureLine label="Date" />
          <SignatureLine label="Printed Name of Recipient" />
        </SimpleGrid>
      </PrintableBlock>
    </Stack>
  );
};

export const ShipmentPackingSlipModal = ({ shipment, ...props }: Props) => (
  <PrintableModal title="Print packing slip" {...props}>
    <PackingSlipContents shipment={shipment} />
  </PrintableModal>
);
